import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContext, useMemo, useState } from 'react';
import { BaseContext } from '../../../shared/base-context';
import { useStableQuery } from '../../../shared/hooks/useStableQuery';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';
import { MainRoutes } from '../../../shared/models';
import { Button } from '../../../catalyst-components/button';
import { Textarea } from '../../../catalyst-components/textarea';
import { useMutation, useAction } from 'convex/react';
import { useLoadingWrapper } from '../../../shared/hooks/useLoadingWrapper';
import { FAQHelpIcon } from '../../../shared/components/FAQHelpIcon';

const isNewSession = (assignedAt?: string) => {
  if (!assignedAt) return false;
  const fourteenDays = 14 * 24 * 60 * 60 * 1000;
  return Date.now() - new Date(assignedAt).getTime() < fourteenDays;
};

export const BrowseAll = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);
  const [isLoading, setIsLoading] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [startingSessionId, setStartingSessionId] = useState<Id<"assistants"> | null>(null);
  const [aiRecommendation, setAiRecommendation] = useState<{ cta_sentence: string, session_ids: string[] } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const LoadingWrapper = useLoadingWrapper({ isLoading });

  // Get all skills
  const skills = useStableQuery(api.skills.list);
  
  // Get all assistant types for filtering (only show reframe and upskill)
  const allAssistantTypes = useStableQuery(api.assistant_types.list);
  const assistantTypes = useMemo(() => {
    if (!allAssistantTypes) return [];
    return allAssistantTypes.filter(type => 
      type.name.toLowerCase() === 'reframe' || type.name.toLowerCase() === 'upskill'
    );
  }, [allAssistantTypes]);
  
  // Get session in progress to disable all session starts
  const sessionInProgress = useStableQuery(
    api.user_progress.getCurrentSessionInProgress,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  const startSession = useMutation(api.sessions.startSession);
  const getCustomRecommendations = useAction(api.user_progress.getCustomRecommendations);

  // Get incomplete assistants for all skills
  const incompleteAssistantsBySkill = useStableQuery(
    api.skill_assistants.getAllIncompleteAssistantsForUser,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  // Filter assistants based on AI recommendations and selected types
  const filteredAssistants = useMemo(() => {
    if (!incompleteAssistantsBySkill) return [];

    let filtered = incompleteAssistantsBySkill;

    // Filter by selected session types
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(assistant =>
        selectedTypes.includes(assistant.assistantType)
      );
    }

    // If we have AI recommendations, hard-filter to only show recommended sessions
    if (aiRecommendation && aiRecommendation.session_ids.length > 0) {
      const recommendedIds = new Set(aiRecommendation.session_ids);
      filtered = filtered.filter(a => recommendedIds.has(a.assistant_id));
    }

    return filtered;
  }, [incompleteAssistantsBySkill, aiRecommendation, selectedTypes]);

  const handleStartSession = async (assistantId: Id<"assistants">) => {
    if (!convexUserId || startingSessionId) return;
    
    try {
      setStartingSessionId(assistantId);
      const result = await startSession({
        user_id: convexUserId as Id<"users">,
        assistant_id: assistantId,
      });
      
      if (result?.session_id) {
        navigate(`${MainRoutes.Chat}?sessionId=${result.session_id}`);
      }
    } catch (error) {
      setStartingSessionId(null);
    }
  };

  const handleSubmitPrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userPrompt.trim() || !convexUserId || isGenerating) return;
    
    try {
      setIsGenerating(true);
      const result = await getCustomRecommendations({
        user_id: convexUserId as Id<"users">,
        user_prompt: userPrompt.trim(),
      });
      
      setAiRecommendation(result);
    } catch (error) {
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearFilters = () => {
    setUserPrompt('');
    setSelectedTypes([]);
    setAiRecommendation(null);
  };

  const toggleType = (typeName: string) => {
    setSelectedTypes(prev => 
      prev.includes(typeName) 
        ? prev.filter(t => t !== typeName)
        : [...prev, typeName]
    );
  };


  return (
    <LoadingWrapper>
      <section className="w-full mx-auto max-w-6xl px-4 pt-24 pb-8">
        {/* Header section */}
        <div className="mb-6">
          <Button
            color="light"
            onClick={() => navigate(MainRoutes.Progress)}
            className="mb-4"
          >
            {t('common.backHome')}
          </Button>
          
          <div className="flex items-center justify-center gap-2 mb-6">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-5 text-center">
              {t('browse.pageTitle')}
            </h1>
            <FAQHelpIcon anchor="sessions" size="md" tooltipText="How do sessions work?" />
          </div>
          
          {/* AI Recommendation Input */}
          <form onSubmit={handleSubmitPrompt} className="w-full max-w-2xl mx-auto mb-6">
            <div className="flex flex-col gap-3">
              <label className="text-primary-5 font-semibold text-sm">
                {t('browse.promptLabel')}
              </label>
              <Textarea
                placeholder={t('browse.promptPlaceholder')}
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                disabled={isGenerating}
                className="w-full"
                style={{
                  minHeight: '3rem',
                  maxHeight: '8rem',
                  resize: 'vertical',
                }}
              />

              <Button
                type="submit"
                color="primary"
                disabled={!userPrompt.trim() || isGenerating}
                className="w-full sm:w-auto sm:self-start h-12 px-6"
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center gap-2">
                    <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-4 w-4" />
                    {t('browse.submitLoading')}
                  </span>
                ) : (
                  t('browse.submitButton')
                )}
              </Button>


              {/* Session Type Filters */}
              {assistantTypes && assistantTypes.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap flex-col gap-2">
                    {assistantTypes.map((type) => {
                      const description = type.name.toLowerCase() === 'reframe' ? t('browse.typeReframeDescription') : t('browse.typeUpskillDescription');
                      return (
                        <div className="flex flex-wrap flex-row items-center gap-2">
                        <button
                          key={type._id}
                          type="button"
                          onClick={() => toggleType(type.name)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border-2 flex items-center gap-2 ${
                            selectedTypes.includes(type.name)
                              ? 'bg-primary-4 border-primary-5 text-white'
                              : 'bg-primary-1 border-primary-3 text-primary-5 hover:border-primary-4'
                          }`}
                        >
                          <span>{type.name}</span>
                        </button>
                        <span className="text-xs opacity-75">{description}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
              )}
              
            </div>
          </form>

          {/* AI Recommendation Response */}
          {aiRecommendation && aiRecommendation.cta_sentence && (
            <div className="w-full max-w-2xl mx-auto mb-6 p-4 bg-primary-1 border-2 border-primary-4 rounded-lg">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-primary-5 font-bold text-base">{t('browse.recommendationLabel')}</h3>
                <Button
                  color="light"
                  onClick={handleClearFilters}
                  className="text-xs px-2 py-1 flex-shrink-0"
                >
                  {t('browse.showAll', 'Show All')}
                </Button>
              </div>
              <div className="text-primary-5 text-base leading-relaxed whitespace-pre-wrap">
                {aiRecommendation.cta_sentence}
              </div>
            </div>
          )}
        </div>

        {/* Session cards */}
        {filteredAssistants && filteredAssistants.length > 0 ? (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4${sessionInProgress?.session_id ? " opacity-50" : ""}`}>
            {filteredAssistants.map((assistant) => {
              const isStarting = startingSessionId === assistant.assistant_id;
              return (
                <div
                  key={assistant._id}
                  className="group relative bg-primary-1 rounded-xl p-4 sm:p-6 border-2 border-primary-4 hover:border-primary-5 transition-all duration-300 hover:shadow-lg flex flex-col"
                >
                  <div className="flex flex-col h-full">
                    {/* Skill name badge */}
                    <div className="mb-2 sm:mb-3 flex flex-wrap gap-2">
                      <span className="inline-block px-2 sm:px-3 py-1 bg-primary-2 border border-primary-4 rounded-full text-xs font-semibold text-primary-5">
                        {assistant.skillName}
                      </span>
                      {isNewSession(assistant.assigned_at) && (
                        <span className="inline-block px-2 sm:px-3 py-1 bg-accent text-accent-foreground rounded-full text-xs font-bold shadow-sm">
                          {t('common.new')}
                        </span>
                      )}
                    </div>

                    <div className="flex-1 mb-3">
                      <div className="flex flex-wrap items-start gap-2 mb-2">
                        <h3 className="text-lg sm:text-xl font-bold text-primary-5 flex-1 min-w-0">
                          {assistant.assistantDisplayName || assistant.name}
                        </h3>
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          <div className="border border-primary-4 rounded-md px-2 py-1 bg-primary-1 text-xs sm:text-sm text-primary-5 whitespace-nowrap">
                            {assistant.assistantType}
                          </div>
                          {assistant.multipleChoice && (
                            <div className="border-2 border-primary-5 rounded-md px-2 py-1 bg-primary-2 text-xs sm:text-sm font-semibold text-primary-5 whitespace-nowrap flex items-center" title="Quiz">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                                <path d="M9 11l3 3L22 4"/>
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                      {assistant.assistantDescription && (
                        <p className="text-primary-5 text-sm opacity-75 line-clamp-3">
                          {assistant.assistantDescription}
                        </p>
                      )}
                    </div>
                    
                    <Button
                      color="primary"
                      onClick={() => handleStartSession(assistant.assistant_id)}
                      disabled={!!sessionInProgress || isStarting}
                      className="mt-auto px-6 py-2"
                    >
                      {isStarting ? (
                        <span className="flex items-center justify-center gap-2">
                          <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-4 w-4" />
                          {t('browse.submitLoading')}
                        </span>
                      ) : (
                        t('browse.submitButton')
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 text-primary-5">
            {aiRecommendation ? (
              <div>
                <p className="text-lg font-semibold mb-2">{t('browse.noResultsWithFilter')}</p>
                <p className="opacity-75">{t('browse.noResultsFilterHint')}</p>
                <Button
                  color="light"
                  onClick={handleClearFilters}
                  className="mt-4"
                >
                  {t('browse.clearFilters')}
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-lg font-semibold mb-2">{t('browse.noSessionsAvailable')}</p>
                <p className="opacity-75">{t('browse.noSessionsHint')}</p>
              </div>
            )}
          </div>
        )}
      </section>
    </LoadingWrapper>
  );
};
